import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, BellRing, Eye, CheckCircle2, AlertTriangle, CircleDashed, Loader2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { MonthInput } from "./DateInput";

export const RECEIPT_STATUS = {
  unread: { label: "Non lu", cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: CircleDashed },
  outdated: { label: "À relire", cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: AlertTriangle },
  viewed: { label: "Consulté", cls: "bg-sky-50 text-sky-800 border-sky-300", Icon: Eye },
  confirmed: { label: "Confirmé", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
export const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

export const ReceiptBadge = ({ status, testid }) => {
  const s = RECEIPT_STATUS[status];
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${s.cls}`}>
      <s.Icon className="h-3 w-3" /> {s.label}
    </span>
  );
};

const Counts = ({ counts }) => (
  <div className="flex flex-wrap gap-2 mb-3" data-testid="receipts-counts">
    {Object.keys(RECEIPT_STATUS).map((k) => (
      <div key={k} className={`px-3 py-1.5 rounded-md border text-xs font-bold ${RECEIPT_STATUS[k].cls}`} data-testid={`receipts-count-${k}`}>
        {RECEIPT_STATUS[k].label} : {counts?.[k] ?? 0}
      </div>
    ))}
  </div>
);

const ReceiptsTable = ({ items }) => (
  <table className="w-full text-sm">
    <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
      <tr><th className="text-left px-3 py-2">Agent</th><th className="text-left px-3 py-2">Vac.</th><th className="text-left px-3 py-2">Statut</th>
        <th className="text-left px-3 py-2">Dernière consultation</th><th className="text-left px-3 py-2">Confirmé le</th><th className="text-left px-3 py-2">Relancé le</th></tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
      {items.map((i) => (
        <tr key={i.agent_id} data-testid={`receipt-row-${i.agent_id}`}>
          <td className="px-3 py-2 font-semibold">{i.name}</td>
          <td className="px-3 py-2">{i.vacations}</td>
          <td className="px-3 py-2"><ReceiptBadge status={i.status} testid={`receipt-status-${i.agent_id}`} /></td>
          <td className="px-3 py-2 text-xs text-slate-600">{fmt(i.viewed_at)}{i.views > 1 ? ` (${i.views}×)` : ""}</td>
          <td className="px-3 py-2 text-xs text-slate-600">{fmt(i.acked_at)}</td>
          <td className="px-3 py-2 text-xs text-slate-600">{fmt(i.reminded_at)}</td>
        </tr>
      ))}
      {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8 text-sm">Aucun agent planifié ce mois-ci.</td></tr>}
    </tbody>
  </table>
);

export const PlanningReceiptsDialog = ({ initialMonth, onClose }) => {
  const [month, setMonth] = useState(initialMonth);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const { lastEvent } = useRealtime();
  const load = useCallback(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    http.get("/planning/read-receipts", { params: { month } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [month]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { if (lastEvent?.event === "planning_read") load(); }, [lastEvent, load]);
  const pending = data ? data.items.filter((i) => i.status !== "confirmed" && i.vacations).length : 0;
  const remind = async () => {
    if (!window.confirm(`Envoyer une relance à ${pending} agent(s) n'ayant pas confirmé leur planning de ${monthLabel(month)} ?`)) return;
    setSending(true);
    try {
      const r = await http.post("/planning/read-receipts/remind", { month });
      toast.success(`Relance envoyée à ${r.data.targets} agent(s) — ${r.data.delivered} notification(s) délivrée(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div data-testid="receipts-dialog" className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-lg font-extrabold">Accusés de lecture du planning</h3>
          <MonthInput data-testid="receipts-month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1 text-sm" />
          <button data-testid="receipts-remind" onClick={remind} disabled={!pending || sending}
                  className="ml-auto inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md transition-colors">
            <BellRing className="h-3.5 w-3.5" /> {sending ? "Envoi…" : `Relancer les non-lus (${pending})`}
          </button>
          <button data-testid="receipts-close" onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 overflow-auto">
          {!data ? <div className="py-12 grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div> : <>
            <Counts counts={data.counts} />
            <ReceiptsTable items={data.items} />
            <p className="text-[11px] text-slate-500 mt-3">« À relire » : le planning a été modifié depuis la dernière consultation ou confirmation de l'agent.</p>
          </>}
        </div>
      </div>
    </div>
  );
};

export const AgentReceiptsSection = ({ agentId }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { http.get(`/agents/${agentId}/read-receipts`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [agentId]);
  if (!items) return null;
  return (
    <div data-testid="agent-receipts-section" className="border border-slate-200 rounded-lg p-4">
      <div className="text-sm font-bold mb-2">Accusés de lecture du planning</div>
      {items.length === 0 ? <div className="text-xs text-slate-500">Aucun planning enregistré.</div> : (
        <div className="divide-y divide-slate-100">
          {items.map((i) => (
            <div key={i.month} data-testid={`agent-receipt-${i.month}`} className="flex items-center gap-3 py-1.5 text-xs">
              <span className="w-32 font-semibold capitalize">{monthLabel(i.month)}</span>
              <ReceiptBadge status={i.status} testid={`agent-receipt-status-${i.month}`} />
              <span className="text-slate-500">{i.vacations} vac.</span>
              <span className="text-slate-500 ml-auto">Vu : {fmt(i.viewed_at)} · Confirmé : {fmt(i.acked_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
